import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { COMPANY } from "@/lib/api";

const links = [
  { to: "/", label: "Home" },
  { to: "/drivers", label: "Drivers" },
  { to: "/investors", label: "Investors" },
  { to: "/dashboard", label: "Dashboard" },
  { to: "/careers", label: "Careers" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();

  useEffect(() => { setOpen(false); }, [loc.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-[#0A0A0A]/80 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group">
          <div className="relative w-8 h-8 rounded-md bg-[#3B82F6]/10 border border-[#3B82F6]/30 flex items-center justify-center">
            <span className="font-display font-bold text-[#3B82F6] text-sm">GK</span>
            <div className="absolute inset-0 rounded-md bg-[#3B82F6]/0 group-hover:bg-[#3B82F6]/10 transition" />
          </div>
          <span className="font-display font-semibold tracking-tight text-white text-lg">
            {COMPANY.short}
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-4 py-2 text-sm rounded-lg transition-colors ${
                  isActive ? "text-white bg-white/5" : "text-[#9CA3AF] hover:text-white hover:bg-white/5"
                }`
              }
              end={l.to === "/"}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Link
            to="/drivers"
            data-testid="nav-cta-apply"
            className="group inline-flex items-center gap-1.5 text-sm text-white/90 hover:text-white"
          >
            Apply to Drive
            <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
          <a
            data-testid="nav-cta-whatsapp"
            href={COMPANY.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#3B82F6] hover:bg-[#2563EB] text-white text-sm font-medium px-4 py-2.5 rounded-lg transition shadow-[0_0_20px_rgba(59,130,246,0.25)] hover:shadow-[0_0_30px_rgba(59,130,246,0.45)]"
          >
            WhatsApp Us
          </a>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((o) => !o)}
          className="lg:hidden text-white p-2 rounded-md hover:bg-white/5"
          aria-label="Menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-panel"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="lg:hidden border-t border-white/10 bg-[#0A0A0A]/95 backdrop-blur-xl"
          >
            <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-1">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `px-3 py-3 rounded-md text-base ${
                      isActive ? "text-white bg-white/5" : "text-[#D1D5DB] hover:bg-white/5"
                    }`
                  }
                  end={l.to === "/"}
                >
                  {l.label}
                </NavLink>
              ))}
              <a
                href={COMPANY.whatsapp}
                data-testid="nav-mobile-cta-whatsapp"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 text-center bg-[#3B82F6] hover:bg-[#2563EB] text-white py-3 rounded-lg"
              >
                WhatsApp Us
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
